import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  AlertTriangle, 
  Gauge, 
  Activity, 
  ChevronLeft, 
  ChevronRight,
  FileText,
  Thermometer,
  Zap,
  Clock,
  Sparkles,
  CheckCircle2,
  Info,
  ArrowRight,
  Eye,
  Settings,
  X,
  Wrench,
  Target,
  BookOpen
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { getScenarioById, getRiskLevelBadge } from "../data/scenarioTemplates";
import { TRANSFORMER_DATA } from "../data/transformerData";
import DetailSlidePanel from "../components/DetailSlidePanel";

const iconMap = {
  sparkles: Sparkles,
  clock: Clock,
  zap: Zap,
  thermometer: Thermometer,
  "alert-triangle": AlertTriangle
};

// Helper function to find an item in TRANSFORMER_DATA by name
const findItemInTransformerData = (itemName, itemType) => {
  // Normalize the search name
  const searchName = itemName.toLowerCase().trim();
  
  // Map scenario test names to transformer data names
  const nameMapping = {
    // Tests
    "dga (dissolved gas analysis)": "dissolved gas analysis (dga)",
    "furan analysis": "furan analysis",
    "sfra (sweep frequency response)": "sfra (sweep frequency response)",
    "power factor / tan delta": "power factor test",
    "power factor test": "power factor test",
    "oil quality & moisture": "oil quality tests",
    "oil quality test": "oil quality tests",
    "infrared thermography": "infrared thermography",
    "thermal imaging": "thermal imaging",
    "bushing power factor": "power factor test",
    "turns ratio test": "turns ratio test",
    "insulation resistance": "insulation resistance",
    "winding resistance": "winding resistance",
    "core ground test": "core ground test",
    "oil dielectric strength": "oil quality tests",
    "oil moisture content": "oil quality tests",
    "cooling system inspection": "cooling performance test",
    "cooling performance test": "cooling performance test",
    "degree of polymerization": "furan analysis",
    // Monitoring
    "online dga monitor": "online dga",
    "online dga": "online dga",
    "bushing monitors": "bushing monitors",
    "bushing monitor": "bushing monitors",
    "partial discharge sensors": "online pd sensors",
    "partial discharge monitoring": "online pd monitoring",
    "online pd sensors": "online pd sensors",
    "winding hot spot monitor": "winding hot spot monitors",
    "winding hot spot monitors": "winding hot spot monitors",
    "continuous thermal monitoring": "fiber optic temp sensors",
    "fiber optic temp sensors": "fiber optic temp sensors",
    "load profile monitoring": "temperature sensors",
    "top oil temperature trending": "temperature sensors",
    "temperature sensors": "temperature sensors",
    "cooling fan/pump monitoring": "motor current monitoring",
    "vibration monitoring": "vibration monitors",
    "ambient temperature monitoring": "temperature sensors",
    "enhanced dga monitoring": "online dga",
    "load monitoring": "temperature sensors"
  };
  
  // Get mapped name or use original
  const mappedName = nameMapping[searchName] || searchName;
  
  for (const component of TRANSFORMER_DATA) {
    // Determine which array to search based on itemType
    let itemsToSearch = [];
    if (itemType === "test") {
      itemsToSearch = component.tests || [];
    } else if (itemType === "monitoring") {
      itemsToSearch = component.monitoring || [];
    } else {
      itemsToSearch = [...(component.failureModes || []), ...(component.tests || []), ...(component.monitoring || [])];
    }
    
    for (const item of itemsToSearch) {
      const itemNameLower = item.name.toLowerCase().trim();
      if (itemNameLower === mappedName || itemNameLower.includes(mappedName) || mappedName.includes(itemNameLower)) {
        return {
          item,
          component,
          itemType: itemType || (component.tests?.includes(item) ? "test" : component.monitoring?.includes(item) ? "monitoring" : "failure_mode")
        };
      }
    }
  }
  
  return null;
};

// Slide components
const OverviewSlide = ({ scenario }) => {
  const IconComponent = iconMap[scenario.icon] || FileText;
  const riskBadge = getRiskLevelBadge(scenario.risk_level);
  
  return (
    <div className="h-full flex flex-col">
      {/* Header - Compact */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${scenario.risk_color}20` }}
          >
            <IconComponent className="w-5 h-5" style={{ color: scenario.risk_color }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{scenario.name}</h1>
            <p className="text-sm text-slate-500">{scenario.shortDescription}</p>
          </div>
        </div>
        <Badge className={`text-xs px-2 py-0.5 ${riskBadge.color} border`}>
          {riskBadge.text}
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 gap-4 flex-1 min-h-0 overflow-y-auto">
        {/* Left - Situation */}
        <div className="space-y-3">
          <Card className="border-slate-200">
            <CardContent className="p-3">
              <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2 text-sm">
                <FileText className="w-3.5 h-3.5 text-blue-600" />
                Situation
              </h3>
              <p className="text-xs text-slate-600 mb-2 leading-relaxed">
                {scenario.situation.summary}
              </p>
              <ul className="space-y-1">
                {scenario.situation.bullet_points.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-xs text-slate-700">
                    <span className="w-1 h-1 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></span>
                    {point}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-3">
              <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2 text-sm">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                Key Concerns
              </h3>
              <ul className="space-y-1">
                {scenario.key_concerns.map((concern, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-xs text-amber-700">
                    <span className="w-1 h-1 rounded-full bg-amber-500 mt-1.5 flex-shrink-0"></span>
                    {concern}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
        
        {/* Right - Specifications */}
        <div className="space-y-3">
          <Card className="border-slate-200">
            <CardContent className="p-3">
              <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2 text-sm">
                <Settings className="w-3.5 h-3.5 text-slate-600" />
                Sample Asset Specifications
              </h3>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-[10px] text-slate-500">Rated Power</p>
                  <p className="font-semibold text-slate-900 text-sm">{scenario.sample_specs.rated_power_mva} MVA</p>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-[10px] text-slate-500">Voltage</p>
                  <p className="font-semibold text-slate-900 text-sm">{scenario.sample_specs.voltage_hv_kv}/{scenario.sample_specs.voltage_lv_kv} kV</p>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-[10px] text-slate-500">Age</p>
                  <p className="font-semibold text-slate-900 text-sm">{scenario.sample_specs.age_years} years</p>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-[10px] text-slate-500">Year</p>
                  <p className="font-semibold text-slate-900 text-sm">{scenario.sample_specs.year_manufactured}</p>
                </div>
                <div className="bg-slate-50 rounded p-2 col-span-2">
                  <p className="text-[10px] text-slate-500">Cooling Type</p>
                  <p className="font-semibold text-slate-900 text-sm">{scenario.sample_specs.cooling_type}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-3">
              <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2 text-sm">
                <Activity className="w-3.5 h-3.5 text-blue-600" />
                Operating Conditions
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white/70 rounded p-2">
                  <p className="text-[10px] text-blue-600">Average Load</p>
                  <p className="font-semibold text-blue-900 text-sm">{scenario.operating_conditions.avg_load_percent}%</p>
                </div>
                <div className="bg-white/70 rounded p-2">
                  <p className="text-[10px] text-blue-600">Peak Load</p>
                  <p className="font-semibold text-blue-900 text-sm">{scenario.operating_conditions.peak_load_percent}%</p>
                </div>
                <div className="bg-white/70 rounded p-2">
                  <p className="text-[10px] text-blue-600">Ambient Temp</p>
                  <p className="font-semibold text-blue-900 text-sm">{scenario.operating_conditions.ambient_temp_c}°C</p>
                </div>
                <div className="bg-white/70 rounded p-2">
                  <p className="text-[10px] text-blue-600">Status</p>
                  <p className="font-semibold text-blue-900 text-xs">{scenario.operating_conditions.status}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const PrescribedTestsSlide = ({ scenario, onViewTestDetails }) => {
  const riskBadge = getRiskLevelBadge(scenario.risk_level);
  
  return (
    <div className="h-full flex flex-col">
      {/* Header - Compact */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Prescribed Diagnostic Tests</h1>
          <p className="text-sm text-slate-500">Recommended tests for: {scenario.name}</p>
        </div>
        <Badge className={`text-xs px-2 py-0.5 ${riskBadge.color} border`}>
          {riskBadge.text}
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 gap-4 flex-1 min-h-0 overflow-y-auto">
        {/* Priority 1 */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-red-500"></div>
            <h2 className="font-semibold text-red-800 text-sm">Priority 1 - Immediate</h2>
            <Badge variant="destructive" className="text-[10px] py-0">Required</Badge>
          </div>
          
          <div className="space-y-2">
            {scenario.prescribed_tests.priority_1.map((test, idx) => (
              <Card 
                key={idx} 
                className="border-red-200 bg-red-50/30 hover:bg-red-50 transition-colors cursor-pointer group"
                onClick={() => onViewTestDetails(test)}
              >
                <CardContent className="p-2.5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 group-hover:text-red-700 transition-colors text-sm">
                        {test.name}
                      </h3>
                      <p className="text-[10px] text-red-600 mb-1">{test.component}</p>
                      <p className="text-xs text-slate-600 line-clamp-2">{test.reason}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0">
                      <Eye className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        
        {/* Priority 2 */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-amber-500"></div>
            <h2 className="font-semibold text-amber-800 text-sm">Priority 2 - Recommended</h2>
            <Badge variant="outline" className="text-[10px] py-0 border-amber-300 text-amber-700">As Needed</Badge>
          </div>
          
          <div className="space-y-2">
            {scenario.prescribed_tests.priority_2.map((test, idx) => (
              <Card 
                key={idx} 
                className="border-amber-200 bg-amber-50/30 hover:bg-amber-50 transition-colors cursor-pointer group"
                onClick={() => onViewTestDetails(test)}
              >
                <CardContent className="p-2.5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 group-hover:text-amber-700 transition-colors text-sm">
                        {test.name}
                      </h3>
                      <p className="text-[10px] text-amber-600 mb-1">{test.component}</p>
                      <p className="text-xs text-slate-600 line-clamp-2">{test.reason}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0">
                      <Eye className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MonitoringSlide = ({ scenario, onViewMonitorDetails }) => {
  const riskBadge = getRiskLevelBadge(scenario.risk_level);
  
  return (
    <div className="h-full flex flex-col">
      {/* Header - Compact */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Recommended Monitoring Systems</h1>
          <p className="text-sm text-slate-500">Continuous monitoring for: {scenario.name}</p>
        </div>
        <Badge className={`text-xs px-2 py-0.5 ${riskBadge.color} border`}>
          {riskBadge.text}
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 gap-4 flex-1 min-h-0 overflow-y-auto">
        {/* Critical */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-green-600"></div>
            <h2 className="font-semibold text-green-800 text-sm">Critical Monitoring</h2>
            <Badge className="text-[10px] py-0 bg-green-100 text-green-800 border-green-300">Essential</Badge>
          </div>
          
          <div className="space-y-2">
            {scenario.prescribed_monitoring.critical.map((monitor, idx) => (
              <Card 
                key={idx} 
                className="border-green-200 bg-green-50/30 hover:bg-green-50 transition-colors cursor-pointer group"
                onClick={() => onViewMonitorDetails(monitor)}
              >
                <CardContent className="p-2.5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 group-hover:text-green-700 transition-colors text-sm">
                        {monitor.name}
                      </h3>
                      <p className="text-[10px] text-green-600 mb-1">{monitor.component}</p>
                      <p className="text-xs text-slate-600 line-clamp-2">{monitor.reason}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0">
                      <Eye className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        
        {/* Recommended */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
            <h2 className="font-semibold text-blue-800 text-sm">Recommended Monitoring</h2>
            <Badge variant="outline" className="text-[10px] py-0 border-blue-300 text-blue-700">Advised</Badge>
          </div>
          
          <div className="space-y-2">
            {scenario.prescribed_monitoring.recommended.map((monitor, idx) => (
              <Card 
                key={idx} 
                className="border-blue-200 bg-blue-50/30 hover:bg-blue-50 transition-colors cursor-pointer group"
                onClick={() => onViewMonitorDetails(monitor)}
              >
                <CardContent className="p-2.5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 group-hover:text-blue-700 transition-colors text-sm">
                        {monitor.name}
                      </h3>
                      <p className="text-[10px] text-blue-600 mb-1">{monitor.component}</p>
                      <p className="text-xs text-slate-600 line-clamp-2">{monitor.reason}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0">
                      <Eye className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const SummarySlide = ({ scenario }) => {
  const riskBadge = getRiskLevelBadge(scenario.risk_level);
  const IconComponent = iconMap[scenario.icon] || FileText;
  
  const totalPriorityTests = scenario.prescribed_tests.priority_1.length;
  const totalRecommendedTests = scenario.prescribed_tests.priority_2.length;
  const totalCriticalMonitoring = scenario.prescribed_monitoring.critical.length;
  const totalRecommendedMonitoring = scenario.prescribed_monitoring.recommended.length;
  
  return (
    <div className="h-full flex flex-col">
      {/* Header - Compact */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${scenario.risk_color}20` }}
          >
            <IconComponent className="w-5 h-5" style={{ color: scenario.risk_color }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Assessment Summary</h1>
            <p className="text-sm text-slate-500">{scenario.name}</p>
          </div>
        </div>
        <Badge className={`text-xs px-2 py-0.5 ${riskBadge.color} border`}>
          {riskBadge.text}
        </Badge>
      </div>
      
      <div className="grid grid-cols-3 gap-4 flex-1 min-h-0 overflow-y-auto">
        {/* Stats */}
        <div>
          <Card className="border-slate-200 h-full">
            <CardContent className="p-3">
              <h3 className="font-semibold text-slate-900 mb-3 text-sm">Prescription Summary</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-red-50 rounded">
                  <div className="flex items-center gap-2">
                    <Gauge className="w-3 h-3 text-red-600" />
                    <span className="text-xs text-red-700">Priority Tests</span>
                  </div>
                  <span className="font-bold text-red-800 text-sm">{totalPriorityTests}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-amber-50 rounded">
                  <div className="flex items-center gap-2">
                    <Gauge className="w-3 h-3 text-amber-600" />
                    <span className="text-xs text-amber-700">Recommended Tests</span>
                  </div>
                  <span className="font-bold text-amber-800 text-sm">{totalRecommendedTests}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                  <div className="flex items-center gap-2">
                    <Activity className="w-3 h-3 text-green-600" />
                    <span className="text-xs text-green-700">Critical Monitoring</span>
                  </div>
                  <span className="font-bold text-green-800 text-sm">{totalCriticalMonitoring}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
                  <div className="flex items-center gap-2">
                    <Activity className="w-3 h-3 text-blue-600" />
                    <span className="text-xs text-blue-700">Recommended Monitoring</span>
                  </div>
                  <span className="font-bold text-blue-800 text-sm">{totalRecommendedMonitoring}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Recommendation */}
        <div className="col-span-2">
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-slate-50 h-full">
            <CardContent className="p-4">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-blue-600" />
                Expert Recommendation
              </h3>
              <p className="text-slate-700 leading-relaxed text-sm">
                {scenario.recommendation_summary}
              </p>
              
              <div className="mt-3 pt-3 border-t border-blue-200">
                <div className="flex items-start gap-2">
                  <Info className="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-600">
                    This assessment is based on the scenario parameters. Actual recommendations 
                    may vary based on specific asset history, manufacturer guidelines, and 
                    regulatory requirements.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Main Page Component
const ScenarioPresentationPage = () => {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [scenario, setScenario] = useState(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [selectedDetailItem, setSelectedDetailItem] = useState(null);
  const [selectedDetailType, setSelectedDetailType] = useState(null);
  const [selectedComponentName, setSelectedComponentName] = useState(null);
  const [selectedComponentColor, setSelectedComponentColor] = useState(null);
  
  // Get the return location from URL params
  const searchParams = new URLSearchParams(window.location.search);
  const returnTo = searchParams.get('returnTo') || '/landing';
  
  const handleExit = () => {
    navigate(returnTo);
  };
  
  useEffect(() => {
    // First try to get from SCENARIO_TEMPLATES
    const found = getScenarioById(scenarioId);
    if (found) {
      setScenario(found);
    } else if (scenarioId?.startsWith('custom_')) {
      // For custom scenarios, check sessionStorage
      const customScenario = sessionStorage.getItem('customScenario');
      if (customScenario) {
        try {
          const parsed = JSON.parse(customScenario);
          if (parsed.id === scenarioId) {
            setScenario(parsed);
            return;
          }
        } catch (e) {
          console.error("Failed to parse custom scenario:", e);
        }
      }
      navigate(returnTo);
    } else {
      navigate(returnTo);
    }
  }, [scenarioId, navigate, returnTo]);
  
  const slides = [
    { id: "overview", label: "Overview" },
    { id: "tests", label: "Prescribed Tests" },
    { id: "monitoring", label: "Monitoring" },
    { id: "summary", label: "Summary" }
  ];
  
  const handlePrevSlide = () => {
    setCurrentSlide(prev => Math.max(0, prev - 1));
  };
  
  const handleNextSlide = () => {
    setCurrentSlide(prev => Math.min(slides.length - 1, prev + 1));
  };
  
  const handleViewTestDetails = (test) => {
    // Try to find matching item in TRANSFORMER_DATA
    const found = findItemInTransformerData(test.name, "test");
    if (found) {
      setSelectedDetailItem(found.item);
      setSelectedDetailType("test");
      setSelectedComponentName(found.component.name);
      setSelectedComponentColor(found.component.color);
      setDetailPanelOpen(true);
    } else {
      // If not found, show a toast or create a basic item
      console.log("Test not found in knowledge base:", test.name);
      // Create a basic item structure for display
      setSelectedDetailItem({
        name: test.name,
        description: test.reason,
        approach: `This test is prescribed for the ${test.component} to address the specific conditions of this scenario.`,
        importance: test.reason,
        images: []
      });
      setSelectedDetailType("test");
      setSelectedComponentName(test.component);
      setSelectedComponentColor("#3b82f6");
      setDetailPanelOpen(true);
    }
  };
  
  const handleViewMonitorDetails = (monitor) => {
    // Try to find matching item in TRANSFORMER_DATA
    const found = findItemInTransformerData(monitor.name, "monitoring");
    if (found) {
      setSelectedDetailItem(found.item);
      setSelectedDetailType("monitoring");
      setSelectedComponentName(found.component.name);
      setSelectedComponentColor(found.component.color);
      setDetailPanelOpen(true);
    } else {
      console.log("Monitor not found in knowledge base:", monitor.name);
      setSelectedDetailItem({
        name: monitor.name,
        description: monitor.reason,
        approach: `This monitoring system is recommended for the ${monitor.component} based on the scenario conditions.`,
        importance: monitor.reason,
        images: []
      });
      setSelectedDetailType("monitoring");
      setSelectedComponentName(monitor.component);
      setSelectedComponentColor("#22c55e");
      setDetailPanelOpen(true);
    }
  };
  
  const handleCloseDetail = () => {
    setDetailPanelOpen(false);
    setSelectedDetailItem(null);
    setSelectedDetailType(null);
    setSelectedComponentName(null);
    setSelectedComponentColor(null);
  };
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Close modal on ESC if open
      if (e.key === "Escape") {
        if (detailPanelOpen) {
          handleCloseDetail();
        } else {
          handleExit();
        }
        return;
      }
      // Arrow navigation only when modal is closed
      if (!detailPanelOpen) {
        if (e.key === "ArrowLeft") handlePrevSlide();
        if (e.key === "ArrowRight") handleNextSlide();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate, detailPanelOpen]);
  
  if (!scenario) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  const renderSlide = () => {
    switch (slides[currentSlide].id) {
      case "overview":
        return <OverviewSlide scenario={scenario} />;
      case "tests":
        return <PrescribedTestsSlide scenario={scenario} onViewTestDetails={handleViewTestDetails} />;
      case "monitoring":
        return <MonitoringSlide scenario={scenario} onViewMonitorDetails={handleViewMonitorDetails} />;
      case "summary":
        return <SummarySlide scenario={scenario} />;
      default:
        return <OverviewSlide scenario={scenario} />;
    }
  };
  
  return (
    <div className="h-screen bg-slate-50 flex flex-col overflow-hidden">
      {/* Top Navigation Bar */}
      <div className="bg-slate-900 text-white px-6 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <span className="font-semibold text-sm">Scenario Assessment</span>
          <span className="text-slate-400">|</span>
          <span className="text-slate-300 text-sm">{scenario.name}</span>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Slide indicators */}
          <div className="flex items-center gap-2">
            {slides.map((slide, idx) => (
              <button
                key={slide.id}
                onClick={() => setCurrentSlide(idx)}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  idx === currentSlide 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                {slide.label}
              </button>
            ))}
          </div>
          
          <span className="text-slate-600">|</span>
          
          <button
            onClick={handleExit}
            className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
            Exit
          </button>
        </div>
      </div>
      
      {/* Main Content - Fixed height with overflow */}
      <main className="flex-1 p-4 max-w-7xl mx-auto w-full overflow-y-auto min-h-0">
        {renderSlide()}
      </main>
      
      {/* Bottom Navigation */}
      <div className="bg-slate-900 text-white px-6 py-2 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={handlePrevSlide}
            disabled={currentSlide === 0}
            className={`flex items-center gap-1 transition-colors text-sm ${
              currentSlide === 0 
                ? 'text-slate-600 cursor-not-allowed' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            {currentSlide > 0 && slides[currentSlide - 1].label}
          </button>
          
          <span className="text-slate-500 text-xs">
            ← → arrow keys to navigate • ESC to exit
          </span>
          
          <button
            onClick={handleNextSlide}
            disabled={currentSlide === slides.length - 1}
            className={`flex items-center gap-1 transition-colors text-sm ${
              currentSlide === slides.length - 1 
                ? 'text-slate-600 cursor-not-allowed' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            {currentSlide < slides.length - 1 && slides[currentSlide + 1].label}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Detail Slide Panel - Same as Transformer Knowledge Base */}
      <DetailSlidePanel
        isOpen={detailPanelOpen}
        onClose={handleCloseDetail}
        item={selectedDetailItem}
        itemType={selectedDetailType}
        componentName={selectedComponentName}
        componentColor={selectedComponentColor}
        relatedItems={null}
        onSelectRelated={null}
      />
    </div>
  );
};

export default ScenarioPresentationPage;
